<template>
  <section class="avue-echart-map device-location-map" :style="mapSectionStyle" role="region" aria-label="设备分布地图" :aria-busy="String(locationStatus === 'loading')">
    <component v-if="Number(option.type) === 1 && Number(dataType) !== 5 && legacyMapRenderer" :is="legacyMapRenderer" v-bind="$props" />
    <div v-show="!locationContentHidden && (Number(option.type) !== 1 || Number(dataType) === 5)" ref="map" class="device-location-map__canvas" />
    <canvas v-show="!locationContentHidden && (Number(option.type) !== 1 || Number(dataType) === 5)" ref="motion" class="device-location-map__motion" aria-hidden="true" />
    <div v-if="mapInset.show && !locationContentHidden" class="device-location-map__inset" :style="{ width: mapInset.width, right: mapInset.right, bottom: mapInset.bottom, color: mapInset.color, borderColor: mapInset.color, backgroundColor: mapInset.background }">
      <svg viewBox="0 0 120 160" role="img" aria-label="南海诸岛附图（地理边界示意，不重复显示设备）">
        <svg x="4" y="4" width="112" height="140" viewBox="0 0 112 140" overflow="hidden">
          <path :d="mapInset.path" :fill="mapInset.fill" :stroke="mapInset.color" stroke-width="0.6" fill-rule="evenodd" />
        </svg>
        <text x="60" y="154" text-anchor="middle" fill="currentColor" font-size="9">南海诸岛</text>
      </svg>
    </div>
  </section>
</template>
<script>
import Vue from 'vue'
import create from '../core/echart/create'
import common from '../core/echart/common'
import locationFeed from './feed-mixin'
import { localBasemap, mapChartOption, clusterMapPoints, clusterViewport, mapRenderOptions, demoMapBackground } from './basemaps'
import { mapInteraction, initialMapView, isRegionCountMode } from './interaction'
import { createRegionCounter } from './region-counts'
import { layoutRegionLabels, regionLabelItem, regionHoverSeries } from './region-labels'
import { registerMapViewport } from '@/runtime/device-map-status'
import { chartHost } from '@/runtime/device-chart-runtime'
import { staticLocationRows } from '@/runtime/device-map-model'
import { highlightGeoRegion, resolveLegacyMap } from './renderer'
import { createMapMotion } from './motion'
import { mainMapFrame, mainMapGeometry, southChinaSeaInset } from './inset'
export default create({
  name: 'map', deviceLocationMap: true, mixins: [locationFeed],
  data: () => ({ mapError: '', mapInsetFrame: null }),
  computed: {
    mapOptions () { return mapRenderOptions(this.option, Number(this.dataType) === 5) },
    mapSectionStyle () {
      const background = demoMapBackground(this.option, Number(this.dataType) === 5)
      return { ...this.styleSizeName, ...(background ? { backgroundImage: 'url(' + JSON.stringify(background) + ')', backgroundSize: 'cover', backgroundPosition: 'center' } : {}) }
    },
    legacyMapRenderer () { return resolveLegacyMap(Vue.options.components) },
    mapInset () { return southChinaSeaInset(this.mapOptions, this.width, this.height, this.mapInsetFrame) }
  },
  watch: {
    mapError (value) { if (this._mapDiagnostic) this._mapDiagnostic.update({ renderError: value }) },
    locationContentHidden (value) { if (!value) this.$nextTick(() => { if (!this._isDestroyed) this.updateChart() }) },
    width () { this.$nextTick(() => { if (!this._isDestroyed) this.updateChart() }) },
    height () { this.$nextTick(() => { if (!this._isDestroyed) this.updateChart() }) }
  },
  mounted () {
    if (Number(this.mapOptions.type) === 1 && Number(this.dataType) !== 5) return
    this.ensureMapChart()
    this._releaseMapViewport = registerMapViewport(chartHost(this), this.id, () => this.readViewport())
    this.updateData()
  },
  beforeDestroy () {
    clearInterval(this._mapBanner)
    clearTimeout(this._mapRoamPaint)
    clearTimeout(this._regionHoverTimer)
    this._regionLabels = null
    this._regionCounter = null
    if (this._mapMotion) this._mapMotion.dispose()
    this._mapMotion = null
    if (this._releaseMapViewport) this._releaseMapViewport()
    if (this.myChart) { this.myChart.dispose(); this.myChart = null }
    // ECharts4没有unregisterMap；清空本实例注册的数据，避免反复切页残留大份GeoJSON缓存。
    if (this._registeredMapName && window.echarts) window.echarts.registerMap(this._registeredMapName, { type: 'FeatureCollection', features: [] })
  },
  methods: {
    ensureMapChart () {
      if (this.myChart || !this._locationMounted || !this.$refs.map) return
      if (!window.echarts) { this.mapError = '图表引擎尚未加载'; return }
      this.myChart = window.echarts.init(this.$refs.map)
      this.myChart.on('georoam', () => {
        const geo = this.myChart.getOption().geo[0]; this._mapViewport = { zoom: geo.zoom, center: geo.center }
        if (this._mapMotion) this._mapMotion.view(this.mapMotionGeometry())
        clearTimeout(this._mapRoamPaint)
        this._mapRoamPaint = setTimeout(() => { if (!this._isDestroyed) this.updateChart() }, 80)
      })
      this.myChart.on('click', event => {
        const row = event.data && event.data.device
        if (!row || !(row.clusterCount > 1 || row.clusterRows && row.clusterRows.length > 1)) return
        this.zoomCluster(row)
      })
      this.myChart.on('mouseover', event => {
        this.rememberMapPointer(event)
        const row = event.data && event.data.device, name = event.componentType === 'geo' ? event.name : row && row.regionCount ? row.name : null
        if (name) {
          if (this._hoverGeoName && this._hoverGeoName !== name) this.highlightMapRegion(false)
          this._hoverGeoName = name; clearTimeout(this._regionHoverTimer)
        }
        this.hoverRegionLabel(event); this.highlightMapRegion(true)
        const region = name && this._regionLabels && this._regionLabels.find(item => item.row.name === name)
        if (this._mapMotion) this._mapMotion.hover(row || (region && region.row))
      })
      this.myChart.on('mouseout', event => {
        this.rememberMapPointer(event); this.clearRegionLabelHover()
        // 区域标签重绘也可能触发mouseout，确认真正离区后再停止悬停涟漪。
        if (this._mapMotion && !isRegionCountMode(this.mapOptions)) this._mapMotion.hover(null)
      })
      this.myChart.on('globalout', () => { this.clearRegionLabelHover(true); if (this._mapMotion) this._mapMotion.hover(null) })
    },
    mapMotionGeometry () {
      const chart = this.myChart, map = this.$refs.map
      let bounds
      if (chart.convertFromPixel) {
        const first = chart.convertFromPixel({ geoIndex: 0 }, [-40, -40])
        const last = chart.convertFromPixel({ geoIndex: 0 }, [map.clientWidth + 40, map.clientHeight + 40])
        if ([first, last].every(point => Array.isArray(point) && point.every(Number.isFinite))) bounds = [Math.min(first[0], last[0]), Math.min(first[1], last[1]), Math.max(first[0], last[0]), Math.max(first[1], last[1])]
      }
      return { width: map.clientWidth, height: map.clientHeight, bounds,
        project: coordinate => chart.convertToPixel({ geoIndex: 0 }, coordinate) }
    },
    hoverRegionLabel (event) {
      if (!isRegionCountMode(this.mapOptions) || !this._regionLabels) return
      clearTimeout(this._regionHoverTimer)
      const row = event.data && event.data.device
      const entry = this._regionLabels.find(item => row && row.regionCount ? item.row.regionId === row.regionId : event.componentType === 'geo' && item.row.name === event.name)
      if (!entry) {
        if (this._hoverRegionId) { this._hoverRegionId = null; this.myChart.setOption({ series: [regionHoverSeries()] }) }
        return
      }
      this._hoverRegionId = entry.row.regionId
      this.myChart.setOption({ series: [regionHoverSeries([regionLabelItem(entry.row, this.mapOptions, entry.layout, true)])] })
    },
    rememberMapPointer (event) {
      const pointer = event && event.event
      if (!pointer) return
      const x = pointer.zrX === undefined ? pointer.offsetX : pointer.zrX, y = pointer.zrY === undefined ? pointer.offsetY : pointer.zrY
      if (Number.isFinite(x) && Number.isFinite(y)) this._mapHoverPointer = [x, y]
    },
    stillHoveringMapRegion () {
      const chart = this.myChart
      if (!this._hoverGeoName || !this._mapHoverPointer || !chart.getModel || !chart.convertFromPixel) return false
      const geo = chart.getModel().getComponent('geo'), region = geo && geo.coordinateSystem && geo.coordinateSystem.getRegion(this._hoverGeoName)
      const coordinate = chart.convertFromPixel({ geoIndex: 0 }, this._mapHoverPointer)
      return !!(region && Array.isArray(coordinate) && coordinate.every(Number.isFinite) && region.contain(coordinate))
    },
    highlightMapRegion (active) {
      highlightGeoRegion(this.myChart, this._hoverGeoName, active)
      if (this._mapMotion && isRegionCountMode(this.mapOptions)) {
        const entry = active && this._regionLabels && this._regionLabels.find(item => item.row.name === this._hoverGeoName)
        this._mapMotion.hover(entry && entry.row)
      }
    },
    clearRegionLabelHover (force = false) {
      clearTimeout(this._regionHoverTimer)
      this._regionHoverTimer = setTimeout(() => {
        // setOption会重绘命中的图形并触发mouseout；鼠标实际仍在该区域时不能取消高亮。
        if (!force && this.stillHoveringMapRegion()) { this.highlightMapRegion(true); return }
        this.highlightMapRegion(false); this._hoverGeoName = null; this._mapHoverPointer = null
        this._hoverRegionId = null
        if (!this._isDestroyed && this.myChart && this._regionLabels && isRegionCountMode(this.mapOptions)) this.myChart.setOption({ series: [regionHoverSeries()] })
      }, 60)
    },
    zoomCluster (row) {
      if (!mapInteraction(this.mapOptions).zoom || row.regionCount) return
      const geo = this.myChart.getOption().geo[0], map = this.$refs.map
      const project = typeof this.myChart.convertToPixel === 'function' ? coordinate => this.myChart.convertToPixel({ geoIndex: 0 }, coordinate) : undefined
      clearTimeout(this._mapRoamPaint)
      this._mapViewport = clusterViewport(row, geo, map.clientWidth, map.clientHeight, project)
      if (this.myChart.dispatchAction) this.myChart.dispatchAction({ type: 'hideTip' })
      this.updateChart()
    },
    readViewport () {
      if (!this.myChart || !this.$refs.map) return undefined
      const geo = this.myChart.getOption().geo[0]
      let center = geo.center
      if (!Array.isArray(center) && this.myChart.convertFromPixel) center = this.myChart.convertFromPixel({ geoIndex: 0 }, [this.$refs.map.clientWidth / 2, this.$refs.map.clientHeight / 2])
      const view = initialMapView({ zoom: geo.zoom, centerLng: center && center[0], centerLat: center && center[1] })
      return view.center ? view : undefined
    },
    updateData () {
      if (Number(this.dataType) === 5) return this.updateLocationFeed()
      if (this._locationFeed) this.stopLocationFeed()
      if (Number(this.mapOptions.type) === 1) return
      if (this.sub) { try { this.sub.unsubscribe() } catch (_) { /* 旧连接已关闭 */ } this.sub = null }
      return common.methods.updateData.call(this)
    },
    renderDeviceLocations (rows, change) { this._mapMotionFeed = { rows, change }; this.updateChart() },
    updateChart () {
      if (Number(this.mapOptions.type) === 1 && Number(this.dataType) !== 5) return
      this.ensureMapChart()
      if (!this.myChart) return
      try {
        const sourceGeo = localBasemap(this.mapOptions), name = 'ironman-map-' + this._uid
        const host = chartHost(this)
        const rows = Number(this.dataType) === 5 ? this.locationRows : staticLocationRows(this.dataChart, this.parameter || {}, host.navigationContext || {}, !!host.menuFlag && !host.isPreview).map(row => ({ ...row, lng: Number(row.lng), lat: Number(row.lat), state: 'unknown' })).filter(row => Number.isFinite(row.lng) && Number.isFinite(row.lat))
        const geo = mainMapGeometry(sourceGeo, this.mapOptions, rows)
        const key = JSON.stringify([this.mapOptions.regionCode, this.mapOptions.mapLevel, this.mapOptions.mapData && this.mapOptions.mapData.label, this.mapOptions.displayMode, this.mapOptions.regionCountLevel, geo !== sourceGeo])
        if (key !== this._basemapKey) {
          window.echarts.registerMap(name, geo)
          this._registeredMapName = name; this._basemapKey = key; this._mapViewport = null; this._regionCounter = null
          this._hoverGeoName = null; this._mapHoverPointer = null
        }
        const configuredView = JSON.stringify(initialMapView(this.mapOptions))
        if (this._mapConfiguredView !== configuredView) { this._mapConfiguredView = configuredView; this._mapViewport = null }
        const dynamic = Number(this.dataType) === 5
        const settings = dynamic ? this.mapOptions : { ...this.mapOptions, pointColor: this.mapOptions.pointColor || this.mapOptions.color }
        if (!this._mapMotion && this.$refs.motion && this.myChart.convertToPixel) this._mapMotion = createMapMotion(this.$refs.motion)
        if (this._mapMotionFeed) {
          if (this._mapMotion) this._mapMotion.observe(this._mapMotionFeed.rows, this._mapMotionFeed.change)
          this._mapMotionFeed = null
        }
        const option = mapChartOption(settings, name, [])
        if (dynamic && (this.option.demoMap === true || this.option.demoMapData) && this.parameter && this.parameter.scope === 'device' && rows.length === 1) {
          if (this._demoFocusedDevice !== rows[0].deviceId) { this._demoFocusedDevice = rows[0].deviceId; this._mapViewport = null }
          if (!this._mapViewport) option.geo.forEach(layer => { layer.center = [rows[0].lng, rows[0].lat] })
        }
        if (this._mapViewport) option.geo.forEach(layer => Object.assign(layer, this._mapViewport))
        const width = this.$refs.map.clientWidth, height = this.$refs.map.clientHeight
        const projectionKey = JSON.stringify([key, option.geo[0].zoom, option.geo[0].center, width, height])
        if (this._mapProjectionChart !== this.myChart || this._mapProjectionKey !== projectionKey) {
          this._mapProjectionChart = this.myChart; this._mapProjectionKey = projectionKey; this._mapProjectionCache = new Map()
          // 先建立底图坐标系，投影用于屏幕网格聚合；之后的普通位置更新复用投影缓存。
          this.myChart.setOption(option, true); this.myChart.resize()
        }
        let renderRows = rows, regionLayouts
        if (isRegionCountMode(this.mapOptions)) {
          if (!this._regionCounter) this._regionCounter = createRegionCounter(sourceGeo)
          renderRows = this._regionCounter.update(rows)
          regionLayouts = layoutRegionLabels(renderRows, settings)
        } else if (dynamic && this.mapOptions.clusterPoints !== false) {
          const cache = this._mapProjectionCache, ids = new Set(rows.map(row => row.deviceId))
          cache.forEach((_, id) => { if (!ids.has(id)) cache.delete(id) })
          const project = typeof this.myChart.convertToPixel !== 'function' ? undefined : (coordinate, row) => {
            const previous = cache.get(row.deviceId)
            if (previous && previous.lng === row.lng && previous.lat === row.lat) return previous.pixel
            const pixel = this.myChart.convertToPixel({ geoIndex: 0 }, coordinate)
            cache.set(row.deviceId, { lng: row.lng, lat: row.lat, pixel }); return pixel
          }
          renderRows = clusterMapPoints(rows, project, { width, height, highlightUntil: this._mapMotion ? id => this._mapMotion.highlightUntil(id) : undefined })
        }
        option.series = mapChartOption(settings, name, renderRows, regionLayouts).series
        this._regionLabels = regionLayouts ? renderRows.map(row => ({ row, layout: regionLayouts.get(String(row.regionId || row.name)) })) : null
        const hovered = this._regionLabels && this._regionLabels.find(item => item.row.regionId === this._hoverRegionId)
        if (hovered) option.series[1] = regionHoverSeries([regionLabelItem(hovered.row, settings, hovered.layout, true)])
        else this._hoverRegionId = null
        this.myChart.setOption(option, true); this.myChart.resize(); this.mapError = ''
        this.mapInsetFrame = this.mapInset.show ? mainMapFrame(geo, typeof this.myChart.convertToPixel === 'function' ? coordinate => this.myChart.convertToPixel({ geoIndex: 0 }, coordinate) : undefined) || null : null
        if (this._mapMotion) this._mapMotion.update(renderRows, settings, this.mapMotionGeometry())
        this.highlightMapRegion(true)
        const bannerKey = JSON.stringify([!!this.mapOptions.banner, this.mapOptions.bannerTime])
        if (bannerKey !== this._bannerKey) {
          this._bannerKey = bannerKey; clearInterval(this._mapBanner)
          if (this.mapOptions.banner) this._mapBanner = setInterval(() => {
            const current = this.myChart && this.myChart.getOption().series || [], target = current.findIndex(series => series.data && series.data.length)
            if (target < 0) return
            this._bannerIndex = ((this._bannerIndex || 0) + 1) % current[target].data.length
            this.myChart.dispatchAction({ type: 'showTip', seriesIndex: target, dataIndex: this._bannerIndex })
          }, Math.max(1000, Number(this.mapOptions.bannerTime) || 3000))
        }
      } catch (error) {
        this.mapError = error.message || '本地地图加载失败'
        if (this._mapMotion) this._mapMotion.update([], {}, { width: 0, height: 0 })
      }
    }
  }
})
</script>
<style>
.device-location-map { position: relative; overflow: hidden; color: #d9edf4; }
.device-location-map__canvas { width: 100%; height: 100%; }
.device-location-map__motion { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
.device-location-map__inset { position: absolute; z-index: 3; box-sizing: border-box; border: 1px solid; pointer-events: none; }
.device-location-map__inset > svg { display: block; width: 100%; height: auto; pointer-events: none; }
</style>
